<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import { watch } from 'vue'
import * as yup from 'yup'
import { type UpdateAdminPayload, updateAdminAction } from '@/api/functions/access/update-admin-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

interface UpdateAdminFormValues {
  full_name: string
  email: string
  password: string | null
  password_confirmation: string | null
  roles: number | null
  status: 'ACTIVATE' | 'DEACTIVATE'
}

const props = defineProps({
  modelValue: Boolean,
  selectedMember: { type: Object, default: null },
  rolesOptions: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])
const toast = useToast()
const queryClient = useQueryClient()

// --- Validation Schema ---
const schema = yup.object({
  full_name: yup.string().required('نام و نام خانوادگی الزامی است').min(3, 'حداقل ۳ کاراکتر').max(100),
  email: yup.string().required('ایمیل الزامی است').email('فرمت ایمیل معتبر نیست'),
  password: yup
    .string()
    .nullable()
    .transform((value) => (value === '' ? null : value))
    .min(8, 'حداقل 8 کاراکتر'),
  password_confirmation: yup
    .string()
    .nullable()
    .transform((value) => (value === '' ? null : value))
    .when('password', {
      is: (val: string) => val && val.length > 0,
      then: (schema) =>
        schema
          .required('تکرار رمز عبور الزامی است')
          .oneOf([yup.ref('password')], 'رمز عبور و تکرار آن باید یکسان باشند'),
      otherwise: (schema) => schema.notRequired(),
    }),
  roles: yup.number().required('انتخاب نقش الزامی است'),
  status: yup.string().required('وضعیت عضو الزامی است'),
})

// --- Form setup ---
const { errors, resetForm, setValues, handleSubmit } = useForm<UpdateAdminFormValues>({
  validationSchema: schema,
  initialValues: {
    full_name: '',
    email: '',
    password: null,
    password_confirmation: null,
    roles: null,
    status: 'ACTIVATE',
  },
})

// Fields
const { value: full_name } = useField('full_name')
const { value: email } = useField('email')
const { value: password } = useField('password')
const { value: password_confirmation } = useField('password_confirmation')
const { value: roles } = useField('roles')
const { value: status } = useField('status')

// --- Watch for selected member ---
// watch(() => props.selectedMember, (member) => {
//   if (!member) {
//     resetForm();
//     return;
//   }

//   setValues({
//     full_name: member.fullName || '',
//     email: member.email || '',
//     password: null,
//     password_confirmation: null,
//     roles: member.roles?.[0]?.id || null,
//     status: member.status === "ACTIVATE" ? "ACTIVATE" : "DEACTIVATE"
//   });
// }, { immediate: true });
const setMemberValues = () => {
  const member = props.selectedMember

  if (!member) {
    resetForm()
    return
  }

  resetForm({
    values: {
      full_name: member.fullName || '',
      email: member.email || '',
      password: null,
      password_confirmation: null,
      roles: member.roles?.[0]?.id ?? null,
      status: member.status === 'ACTIVATE' ? 'ACTIVATE' : 'DEACTIVATE',
    },
  })
}

watch(
  () => [props.selectedMember, props.modelValue],
  ([member, isOpen]) => {
    if (isOpen && member) {
      setMemberValues()
    }
  },
  {
    immediate: true,
    deep: true,
  }
)

// --- Mutation ---
const updateMemberMutation = useMutationApi({
  mutationFn: (payload: UpdateAdminPayload) => updateAdminAction(payload),
  onSuccess: () => {
    toast.success('اطلاعات عضو با موفقیت ویرایش شد', 'موفقیت')
    emit('confirm')
    closeModal()
    queryClient.invalidateQueries({ queryKey: ['admins'] })
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// --- Submit ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSubmit = handleSubmit((values) => {
  if (!props.selectedMember?.id) return
  if (values.roles === null) return

  const payload: UpdateAdminPayload = {
    id: props.selectedMember.id,
    full_name: values.full_name,
    email: values.email,
    roles: values.roles,
    status: values.status,
  }

  if (values.password) {
    payload.password = values.password
    payload.password_confirmation = values.password_confirmation
  }

  updateMemberMutation.mutate(payload)
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" title="ویرایش عضو" closeDirection="left" class="update-request">

    <form @submit.prevent="onSubmit" class="flex flex-col min-h-[450px]">

      <div class="flex-1">
        <div class="lg:grid grid-cols-2 px-4 pt-6 pb-9 gap-6">
          <Input v-model="full_name" name="full_name" label="نام و نام خانوادگی را وارد کنید" type="text"
            :error="errors.full_name" />
          <Input v-model="email" name="email" label="ایمیل را وارد کنید" type="email" :error="errors.email" />

          <Input v-model="password" name="password" label="رمز ورود " type="password" :error="errors.password" />
          <Input v-model="password_confirmation" name="password_confirmation" label="تکرار رمز ورود " type="password"
            :error="errors.password_confirmation" />

          <Select variant="checkbox" v-model="roles" name="roles" label="نقش را انتخاب کنید" :options="rolesOptions"
            :error="errors.roles" />
          <Select variant="checkbox" v-model="status" label="وضعیت عضو" :options="[
            { label: 'فعال', value: 'ACTIVATE' },
            { label: 'غیر فعال', value: 'DEACTIVATE' }
          ]" :error="errors.status" />
        </div>

      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="ویرایش" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="updateMemberMutation.isPending.value" :loading="updateMemberMutation.isPending.value" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="updateMemberMutation.isPending.value" />
      </div>
    </form>
  </Modal>
</template>
